"use client";

import * as React from "react";
import { PusherEvent, TodoTaskCompletedPayload, TodoTaskCreatedPayload } from "@/lib/pusher/pusher-tools";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "@heroui/modal";
import { useUserLocale } from "@/hooks/useUserLocale";
import { Button } from "@/components/ui/Button";
import { XpGainedDetailedBadge } from "@/components/ui/xp/XpGainedDetailedBadge";
import { EventQueueItem } from "@/providers/ServiceModalsProviderEventQueueItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { todoListToolsUi } from "@/lib/todo/todo-list-tools-ui";
import { TodoItem } from "../Types";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { Avatar } from "@heroui/avatar";
import { ContactCharacter } from "@/lib/types/story";
import { storyFrontendApi } from "@/lib/api-story";
import { frontendApi } from "@/lib/api-requests";
import { LANGUAGES } from "@/lib/constants";
import { useSfxProvider } from "@/hooks/useSfxProvider";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { isBlank } from "@/lib/utils";
import { appTutorialService } from "@/lib/services/TutorialService";

export default function TodoTaskCreatedModal({ isOpen, onClose, event }: { isOpen: boolean, onClose: () => void, event: EventQueueItem }) {
    const { t, locale } = useUserLocale();

    const todoLists = useSelector((state: RootState) => state.app.todoLists);
    const currentUser = useSelector((state: RootState) => state.app.currentUser);
    const contacts = useSelector((state: RootState) => state.app.languageProgress.contacts);
    const isMobileScreen = useSelector((state: RootState) => state.app.isMobileScreen);

    const eventPayload = event.payload as TodoTaskCreatedPayload;
    const payloadTodoItem = useMemo(() => eventPayload.todoItem, [eventPayload]);
    const localTodoItem = useMemo(() => todoLists[eventPayload.listTag]?.items.find(item => item._id.toString() === payloadTodoItem?._id.toString()), [todoLists, eventPayload.listTag, payloadTodoItem]);
    const [detailedToDoItem, setDetailedToDoItem] = useState<TodoItem | null>(null);
    const [character, setCharacter] = useState<ContactCharacter | null>(null);
    const longPlaySoundRef = useRef<string | null>(null);

    const router = useRouter();

    const sfxProvider = useSfxProvider();

    const loadCharacter = useCallback(async () => {
        const character = await storyFrontendApi.characters.getContactCharacter({ characterId: payloadTodoItem?.notification?.characterId || payloadTodoItem?.chatConfig.characterId });
        setCharacter(character);
    }, [payloadTodoItem]);

    const loadDetailedTodoItem = useCallback(async () => {
        const todoItem = await frontendApi.todoLists.getTodoItem({listTag: eventPayload.listTag, itemId: payloadTodoItem?._id.toString(), language: currentUser?.learningLanguage as keyof typeof LANGUAGES, generateNotes: false});
        setDetailedToDoItem(todoItem);
    }, [payloadTodoItem]);
    
    const handleClickableAction = useCallback(() => {
        const clickableUrl = payloadTodoItem?.clickableUrl || detailedToDoItem?.clickableUrl;
        if (clickableUrl) {
            router.push(todoListToolsUi.replaceTodoItemUrlTokens(clickableUrl.url, detailedToDoItem as unknown as Partial<TodoItem>));
        }

        // Close this modal
        onClose();
    }, [payloadTodoItem, detailedToDoItem, onClose, router]);

    useEffect(() => {
        if (payloadTodoItem?.chatConfig?.characterId && !character) {
            loadCharacter().then(_ => {});
        }

        if (payloadTodoItem?.notification?.mood === "bad") {
            longPlaySoundRef.current = "/sfx/bgm-bad-omens.mp3";
            sfxProvider.playSfx("/sfx/bgm-bad-omens.mp3");
        } else {
            longPlaySoundRef.current = null;
            sfxProvider.playSfx("/sfx/new-message.mp3");
        }

        loadDetailedTodoItem().then(_ => {});

        todoListToolsUi.setLocalNotificationCreated(payloadTodoItem?._id.toString(), true);
        
        return () => {
            if (longPlaySoundRef.current) {
                sfxProvider.stopSfx(longPlaySoundRef.current);
            }
        };
    }, [payloadTodoItem]);

    useEffect(() => {
        if (!isOpen) {
            setCharacter(null);
        } else {
            appTutorialService.queueMessage({
                fragmentId: 'firstTask1',
                language: currentUser?.motherLanguage,
                modalId: 'TodoTaskCreatedModal',
            });
        
            appTutorialService.queueMessage({
                fragmentId: 'firstTask2',
                language: currentUser?.motherLanguage,
                modalId: 'TodoTaskCreatedModal',
            });
        
            appTutorialService.queueMessage({
                fragmentId: 'firstTask3',
                language: currentUser?.motherLanguage,
                modalId: 'TodoTaskCreatedModal',
            });
        }
    }, [isOpen]);

    const avatarUrl = useMemo(() => {
        if (detailedToDoItem?.notification?.characterId || payloadTodoItem?.notification?.characterId) {
            console.log("🔍 DetailedToDoItem notification character ID:", detailedToDoItem?.notification?.characterId, "PayloadTodoItem notification character ID:", payloadTodoItem?.notification?.characterId);
            const contact = contacts.find(contact => contact.characterId == (detailedToDoItem?.notification?.characterId || payloadTodoItem?.notification?.characterId));
            console.log("🔍 Contact:", contact);
            return contact?.avatarUrl;
        }
        return detailedToDoItem?.notification?.avatarUrl || detailedToDoItem?.chatConfig?.avatarUrl || character?.avatarUrl || payloadTodoItem?.chatConfig?.avatarUrl;
    }, [payloadTodoItem, detailedToDoItem, character, contacts]);

    return (
        <Modal isOpen={isOpen} size={'lg'} onOpenChange={onClose} placement="top-center" classNames={{backdrop: 'bg-white/75 backdrop-blur-sm', base: 'shadow-none border-2', closeButton: 'text-3xl'}}>
            <ModalContent>
                <ModalHeader>                    
                    {t.todoLists.modals.taskCreated.title}
                </ModalHeader>
                <ModalBody className="max-h-[60vh] sm:max-h-[75vh] overflow-y-auto">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-4">
                            {/* Character info */}
                            <div className="flex flex-row items-center gap-4">
                                <Avatar src={avatarUrl} alt={'character avatar'} size='lg' />
                                <div className="flex flex-col gap-1">
                                    <div>{character?.name}</div>
                                    <div className="text-sm text-gray-500">{character?.description}</div>
                                </div>
                            </div>

                            {/* Optional notification from the character */}
                            {detailedToDoItem?.notification && !isBlank(detailedToDoItem?.notification.text) && <div className="flex flex-col gap-2">
                                <div className="text-sm text-gray-500 border-l-2 border-l-primary pl-2 font-italic">{detailedToDoItem.notification.text}</div>
                            </div>}


                            {/* Task info */}
                            <div className="flex flex-row items-center gap-2">
                                <div>{payloadTodoItem?.emoji || detailedToDoItem?.emoji}</div>
                                <div>{payloadTodoItem?.title || detailedToDoItem?.title}</div>
                            </div>                            
                            {localTodoItem && !detailedToDoItem?.notesHtml && <div className="flex flex-col gap-2">
                                <div className="word-explanation-content">{localTodoItem.description}</div>
                            </div>}

                            {detailedToDoItem && detailedToDoItem.notesHtml && <div className="flex flex-col gap-2 min-h-24 max-h-64 overflow-y-auto word-explanation-content">
                                <div dangerouslySetInnerHTML={{ __html: detailedToDoItem.notesHtml ?? '' }} />
                            </div>}
                        </div>                        
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className='flex items-center gap-2 justify-between w-full'>                        
                        <Button onPress={() => onClose()} color='default' variant='flat' isIconOnly>                            
                            <XMarkIcon className="w-5 h-5" />
                        </Button>
                        <div></div>
                        {(payloadTodoItem?.clickableUrl || detailedToDoItem?.clickableUrl) && 
                            <Button isLoading={!detailedToDoItem} isDisabled={!detailedToDoItem || detailedToDoItem?.completed} onPress={handleClickableAction} size='lg' color='lushPrimary' variant='shadow'>{payloadTodoItem?.clickableUrl?.text || detailedToDoItem?.clickableUrl.text}</Button>}
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
